import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const DashboardContext = createContext(null);

export const useDashboard = () => useContext(DashboardContext);

const ALL_PROGRAMS_OPTION = 'All Programs';
const DEFAULT_PAGE_SIZE = 100;

export const DashboardProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const parseNumberParam = (name, fallback = 0) => {
    const value = Number.parseInt(searchParams.get(name) || '', 10);
    return Number.isFinite(value) ? value : fallback;
  };

  const initialTab = parseNumberParam('tab', 0);
  const programParam = searchParams.get('program');
  const initialSearchType = searchParams.get('type') || (programParam ? 'program' : 'program');
  const rawSearchValue = searchParams.get('value') || (initialSearchType === 'program' ? programParam : null);
  const initialSearchValue =
    initialSearchType === 'program'
      ? rawSearchValue || ALL_PROGRAMS_OPTION
      : rawSearchValue || '';

  const filterProgram = searchParams.get('filterProgram');
  const filterValue = searchParams.get('filterValue');
  const initialActiveFilter = filterProgram && filterValue ? { programName: filterProgram, value: filterValue } : null;

  const initialPage = parseNumberParam('page', 0);
  const initialPageSize = parseNumberParam('pageSize', DEFAULT_PAGE_SIZE);

  const [searchType, setSearchType] = useState(initialSearchType);
  const [searchValue, setSearchValue] = useState(initialSearchValue);
  const [activeFilter, setActiveFilter] = useState(initialActiveFilter);
  const [currentTab, setCurrentTab] = useState(initialTab);
  const [navTabChangeSignal, setNavTabChangeSignal] = useState(0);
  const [isTableMaximized, setIsTableMaximized] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: initialPage,
    pageSize: initialPageSize,
  });

  const resetDashboard = () => {
    setSearchType('program');
    setSearchValue(ALL_PROGRAMS_OPTION);
    setActiveFilter(null);
    setCurrentTab(0);
    setNavTabChangeSignal((prev) => prev + 1);
    setIsTableMaximized(false);
    setIsDetailOpen(false);
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  };

  const requestTabChange = (nextTab) => {
    setCurrentTab(nextTab);
    setNavTabChangeSignal((prev) => prev + 1);
  };

  useEffect(() => {
    // Preserve unknown query params (e.g., ?search=, ?action=, ?referenceId=) while keeping
    // dashboard-owned params in sync with state.
    const params = new URLSearchParams(searchParams);

    if (currentTab) params.set('tab', String(currentTab));
    else params.delete('tab');

    if (searchType && searchType !== 'program') params.set('type', searchType);
    else params.delete('type');

    // Program filter: support both legacy (?type=program&value=Foo) and friendly (?program=Foo).
    if (searchType === 'program') {
      if (searchValue && searchValue !== ALL_PROGRAMS_OPTION) {
        params.set('value', searchValue);
        params.set('program', searchValue);
      } else {
        params.delete('value');
        params.delete('program');
      }
    } else {
      params.delete('program');
      if (searchValue) params.set('value', searchValue);
      else params.delete('value');
    }

    if (activeFilter?.programName && activeFilter?.value) {
      params.set('filterProgram', activeFilter.programName);
      params.set('filterValue', activeFilter.value);
    } else {
      params.delete('filterProgram');
      params.delete('filterValue');
    }

    if (paginationModel?.page) params.set('page', String(paginationModel.page));
    else params.delete('page');

    if (paginationModel?.pageSize && paginationModel.pageSize !== DEFAULT_PAGE_SIZE) {
      params.set('pageSize', String(paginationModel.pageSize));
    } else {
      params.delete('pageSize');
    }

    if (params.toString() === searchParams.toString()) return;
    setSearchParams(params, { replace: true });
  }, [activeFilter, currentTab, paginationModel, searchParams, searchType, searchValue, setSearchParams]);

  const value = useMemo(
    () => ({
      searchType,
      setSearchType,
      searchValue,
      setSearchValue,
      activeFilter,
      setActiveFilter,
      currentTab,
      setCurrentTab,
      navTabChangeSignal,
      requestTabChange,
      isTableMaximized,
      setIsTableMaximized,
      isDetailOpen,
      setIsDetailOpen,
      paginationModel,
      setPaginationModel,
      resetDashboard,
    }),
    [searchType, searchValue, activeFilter, currentTab, navTabChangeSignal, isTableMaximized, isDetailOpen, paginationModel]
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};
